import { useRouter } from 'next/router';

import type { GTMEventGlycerinSort, GenericTrackingEvent } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { decodeOrder, encodeOrder, pruneQuery } from '../queryEncoding';
import { getGlycerinSortMethod } from '../utils/tracking/getGlycerinSortMethod';

export const UseSortingFragment = graphql(/* GraphQL */ `
  fragment UseSortingFragment on SearchProductResult {
    __typename
    defaultSortingKey
  }
`);

/**
 * Use sorting option persisted in the url query
 *
 * Changing sorting results in a router.push, which will trigger a data reload
 */
export const useSorting = (maskedData: FragmentType<typeof UseSortingFragment>) => {
  const { __typename: pageType, defaultSortingKey } = getFragmentData(
    UseSortingFragment,
    maskedData,
  );

  const dispatchGtmEvent = useTracking();
  const { push, pathname, query } = useRouter();

  const sortingKey = decodeOrder(query) ?? defaultSortingKey;

  const pushSortingChange = (newOrder: string) => {
    if (newOrder === sortingKey) return;

    push(
      {
        // currently the /c/ prefix is internal-only, and rewritten in the middleware
        pathname: pathname.replace(/^\/c/, ''),
        query: pruneQuery({
          ...query,
          ...encodeOrder(newOrder === defaultSortingKey ? undefined : newOrder),
        }),
      },
      undefined,
      { scroll: false },
    );

    dispatchGtmEvent<GTMEventGlycerinSort>({
      event: 'Sort',
      SortData: {
        sortMethod: getGlycerinSortMethod(newOrder, pageType),
      },
    });

    dispatchGtmEvent<GenericTrackingEvent>({
      event: 'ga-event',
      eventAction: 'Filter / Sort benutzt',
      eventCategory: 'User Interactions',
      eventLabel: 'sortiert',
    });
  };

  return {
    sortingKey,
    pushSortingChange,
  };
};
